import { ButtonGhost, ButtonSecondary, Colors, Icon, Text, TextInput } from '@amelio/ui-library';
import { Controller, useFieldArray, type Control } from 'react-hook-form';

import { PLAN_FIELD_VARIANT } from './meetingPlanFieldVariant';
import { LIST_FIELD_LANE, LIST_FIELD_SIZE } from './meetingPlanListLayout';
import { MeetingPlanSectionCard } from './MeetingPlanSectionCard';
import {
    LIST_ENTRY_MAX_LENGTH,
    LIST_MAX_ENTRIES,
    MeetingPlanTemplateFormFields,
    TIP_TITLE_MAX_LENGTH,
    type MeetingPlanTemplateFormData,
} from './validation';

import type { ReactNode } from 'react';

import { useAmelioTranslations } from '@/hooks';
import { validationMessageKey } from '@/utils/validationMessage';

/**
 * One field of a piece of guidance, its label on the left. A row of the tip's table, so every label
 * takes the width of the longest one — whatever the language — and both fields start on one line.
 */
const TipRow = ({
    fieldId,
    label,
    required = false,
    children,
}: {
    fieldId: string;
    label: string;
    required?: boolean;
    children: ReactNode;
}) => (
    <div className={'table-row'}>
        <div className={'table-cell pr-3 align-top'}>
            <div className={`${LIST_FIELD_LANE} gap-0.5`}>
                <Text
                    as={'label'}
                    htmlFor={fieldId}
                    weight={'medium'}
                    className={'whitespace-nowrap'}
                >
                    {label}
                </Text>

                {required && (
                    <span
                        aria-hidden={'true'}
                        style={{ color: Colors.ERROR }}
                    >
                        *
                    </span>
                )}
            </div>
        </div>

        <div className={'table-cell w-full align-top'}>{children}</div>
    </div>
);

type MeetingPlanConversationTipFieldsProps = {
    control: Control<MeetingPlanTemplateFormData>;
    dataTestId: string;
};

/**
 * The guidance the plan offers for the conversation itself: a short heading and the advice under it,
 * each on one line, labelled on the left.
 */
export const MeetingPlanConversationTipFields = ({
    control,
    dataTestId,
}: MeetingPlanConversationTipFieldsProps) => {
    const { t } = useAmelioTranslations();
    const { fields, append, remove } = useFieldArray({
        control,
        name: MeetingPlanTemplateFormFields.conversationTips,
    });
    const errorMessageOf = (message: string | undefined) =>
        message ? t(validationMessageKey(message)) : undefined;

    return (
        <MeetingPlanSectionCard
            title={t('templateManagement.meetingPlanEditor.conversationTipsHeading')}
            dataTestId={dataTestId}
            action={
                <ButtonSecondary
                    size={'sm'}
                    onClick={() => append({ title: '', description: '', storedText: null })}
                    disabled={fields.length >= LIST_MAX_ENTRIES}
                    fitContent
                    leftIcon={
                        <Icon
                            name={'essential-add'}
                            size={'sm'}
                            color={'inherit'}
                            ariaHidden
                        />
                    }
                    dataTestId={`${dataTestId}-add`}
                >
                    {t('templateManagement.meetingPlanEditor.addConversationTip')}
                </ButtonSecondary>
            }
        >
            <div className={'flex flex-col gap-3'}>
                {fields.map((field, index) => {
                    const titleId = `${dataTestId}-${index}-title-field`;
                    const descriptionId = `${dataTestId}-${index}-description-field`;

                    return (
                        <div
                            key={field.id}
                            className={
                                'border-base-200 flex items-start gap-3 rounded-xl border bg-white p-3'
                            }
                            data-testid={`${dataTestId}-${index}`}
                        >
                            {/* The table's row spacing pads its top and bottom as well; the negative
                                margin gives that back to the card's own padding. */}
                            <div
                                className={'-my-2 table w-full border-separate border-spacing-y-2'}
                            >
                                <TipRow
                                    fieldId={titleId}
                                    label={t('templateManagement.meetingPlanEditor.tipTitleLabel')}
                                    required
                                >
                                    <Controller
                                        control={control}
                                        name={`conversationTips.${index}.title`}
                                        render={({ field: tip, fieldState }) => (
                                            <TextInput
                                                variant={PLAN_FIELD_VARIANT}
                                                id={titleId}
                                                value={tip.value}
                                                onChange={tip.onChange}
                                                maxLength={TIP_TITLE_MAX_LENGTH}
                                                size={LIST_FIELD_SIZE}
                                                error={Boolean(fieldState.error)}
                                                errorMessage={errorMessageOf(
                                                    fieldState.error?.message,
                                                )}
                                                dataTestId={`${dataTestId}-${index}-title`}
                                            />
                                        )}
                                    />
                                </TipRow>

                                <TipRow
                                    fieldId={descriptionId}
                                    label={t(
                                        'templateManagement.meetingPlanEditor.tipDescriptionLabel',
                                    )}
                                >
                                    <Controller
                                        control={control}
                                        name={`conversationTips.${index}.description`}
                                        render={({ field: tip, fieldState }) => (
                                            <TextInput
                                                variant={PLAN_FIELD_VARIANT}
                                                id={descriptionId}
                                                value={tip.value}
                                                onChange={tip.onChange}
                                                maxLength={LIST_ENTRY_MAX_LENGTH}
                                                size={LIST_FIELD_SIZE}
                                                error={Boolean(fieldState.error)}
                                                errorMessage={errorMessageOf(
                                                    fieldState.error?.message,
                                                )}
                                                dataTestId={`${dataTestId}-${index}-description`}
                                            />
                                        )}
                                    />
                                </TipRow>
                            </div>

                            <div className={LIST_FIELD_LANE}>
                                <ButtonGhost
                                    size={'sm'}
                                    onClick={() => remove(index)}
                                    aria-label={t(
                                        'templateManagement.meetingPlanEditor.removeConversationTip',
                                    )}
                                    dataTestId={`${dataTestId}-${index}-remove`}
                                >
                                    <Icon
                                        name={'essential-trash'}
                                        size={'sm'}
                                        color={Colors.BASE_500}
                                        ariaHidden
                                    />
                                </ButtonGhost>
                            </div>
                        </div>
                    );
                })}
            </div>
        </MeetingPlanSectionCard>
    );
};
